import {
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  Ref,
  useEffect,
  useState,
} from "react";
import { useTrackingId } from "../../hooks";
import { DataTrackingId } from "../../types";
import { Button, ButtonProps } from "../Button/Button";
import { childrenToString } from "../../internal/functions";
import { useLayoutPropsUtil } from "../../internal/hooks";

import cx from "classnames";
import styles from "../Button/Button.module.scss";

export type ButtonToggleState = {
  pressed: boolean;
  value?: string;
};

/**
 * Props for the ButtonToggle component
 * @extends Omit<ButtonProps, "appearance" | "loading">
 */
export type ButtonToggleProps = Omit<ButtonProps, "appearance" | "loading"> & {
  /**
   * The default pressed(selected) state of the toggle button.
   * @default false
   */
  defaultChecked?: boolean;

  /**
   * The controlled pressed(selected) state of the toggle button.
   */
  checked?: boolean;

  /**
   * The value of the toggle button.
   */
  value?: string;

  /**
   * Callback for when pressed(selected) changes.
   */
  onChange?: (
    e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
    state: ButtonToggleState,
  ) => void;
} &
  /**
   * Data tracking id
   */
  DataTrackingId;

/**
 * ButtonToggle component for creating toggleable button states.
 *
 * Features:
 * - Supports both controlled and uncontrolled state management
 * - Toggle state with pressed/selected visual feedback
 * - Full accessibility support with aria-pressed attribute
 * - Callback support for state changes
 * - Supports layout utilities for positioning and spacing
 * - Inherits all button features except appearance and loading
 * - Flexible content with text and/or icons
 * - Keyboard interaction support
 * - Value association for form-like behavior
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <ButtonToggle
 *   defaultChecked={false}
 *   onChange={(e, state) => console.log('Toggle state:', state.pressed)}
 * >
 *   Toggle Me
 * </ButtonToggle>
 */
export const ButtonToggle = forwardRef(
  (props: ButtonToggleProps, ref: Ref<HTMLButtonElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      value,
      defaultChecked = false,
      checked,
      className,
      onClick,
      onChange,
      onKeyDown,
      style,
      icon,
      size,
      type,
      ...rest
    } = componentProps;

    const [isPressed, setIsPressed] = useState(checked ?? defaultChecked);

    const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      if (typeof checked === "undefined") {
        setIsPressed(!isPressed);
        onChange?.(e, { pressed: !isPressed, value });
      }
      onClick?.(e);
    };

    useEffect(() => {
      if (typeof checked === "undefined") return;
      setIsPressed(checked);
    }, [checked]);

    const data = {
      children: childrenToString(props.children),
      icon,
      size,
      value,
      type,
    };

    const trackingId = useTrackingId({
      name: "ButtonToggle",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const ButtonToggleClassNames = cx(className, styles["button-toggle"]);

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };
    return (
      <Button
        data-tracking-id={trackingId}
        data-anv="button-toggle"
        aria-pressed={isPressed}
        onClick={onClickHandler}
        className={ButtonToggleClassNames}
        value={value}
        style={styleCombined}
        icon={icon}
        size={size}
        type={type}
        ref={ref}
        {...rest}
      />
    );
  },
);

ButtonToggle.displayName = "ButtonToggle";
